import type { Margin } from "~/v2/api/rest/account";

export interface BalanceRow {
  id: string;
  section: string;
  label: string;
  tooltip: string;
  previousCloseLabel: string;
  currentLabel: string;
}

interface BalanceField {
  key: keyof Margin;
  label: string;
  section: string;
  tooltip: string;
}

const BALANCE_FIELDS: readonly BalanceField[] = [
  {
    section: "Buying power",
    key: "buying_power",
    label: "Buying Power",
    tooltip: "Buying power for marginable assets.",
  },
  {
    section: "Buying power",
    key: "options_buying_power",
    label: "Options Buying Power",
    tooltip: "Buying power for options trading.",
  },
  {
    section: "Buying power",
    key: "non_marginable_buying_power",
    label: "Non-Marginable Buying Power",
    tooltip:
      "Buying power for assets that cannot be purchased on margin, such as crypto.",
  },
  {
    section: "Cash",
    key: "cash",
    label: "Cash",
    tooltip:
      "Cash (settled and unsettled), adjusted for outgoing transfers and pending fees.",
  },
  {
    section: "Cash",
    key: "cash_withdrawable",
    label: "Cash Withdrawable",
    tooltip: "Cash you can withdraw or transfer out right now.",
  },
  {
    section: "Portfolio",
    key: "equity",
    label: "Total Equity",
    tooltip:
      "Total account value: cash plus the market value of all positions (long and short).",
  },
  {
    section: "Portfolio",
    key: "long_market_value",
    label: "Long Market Value",
    tooltip: "Current market value of all long positions.",
  },
  {
    section: "Portfolio",
    key: "short_market_value",
    label: "Short Market Value",
    tooltip: "Current market value of all short positions.",
  },
  {
    section: "Margin",
    key: "initial_margin",
    label: "Initial Margin",
    tooltip:
      "Reg T initial margin requirement for your open positions. Updates as prices and positions change.",
  },
  {
    section: "Margin",
    key: "maintenance_margin",
    label: "Maintenance Margin",
    tooltip:
      "Minimum equity required to keep your current positions open. If equity falls below this, you may face a margin call.",
  },
];

export const formatBalanceMoney = (
  value: unknown,
  currency = "USD",
): string => {
  if (value == null || value === "") {
    return "-";
  }

  const amount = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(amount)) {
    return "-";
  }

  try {
    return amount.toLocaleString("en-US", {
      style: "currency",
      currency,
    });
  } catch {
    return amount.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
    });
  }
};

export const toBalanceRows = (
  current?: Margin | null,
  previousClose?: Margin | null,
): BalanceRow[] => {
  if (!current) {
    return [];
  }

  const currency = current.currency || "USD";

  return BALANCE_FIELDS.map((field) => {
    const currentValue = current[field.key];
    const previousValue = previousClose?.[field.key];

    return {
      id: String(field.key),
      section: field.section,
      label: field.label,
      tooltip: field.tooltip,
      previousCloseLabel: formatBalanceMoney(previousValue, currency),
      currentLabel: formatBalanceMoney(currentValue, currency),
    };
  });
};

export const formatBalanceAsOf = (
  value: string | null | undefined,
): string | undefined => {
  if (!value || Number.isNaN(Date.parse(value))) {
    return undefined;
  }

  return new Date(value).toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
    year: "numeric",
  });
};
